<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    ButtonDropdown,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';

  import { DomainCard } from '@client/components/domain-card';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { Loader } from '@client/components/loader';
  import { Pagination } from '@client/components/pagination';

  import { DomainService } from '@client/services/domain';
  import { DomainsService } from '@client/services/domains';

  import { staffSettingsStore } from '@client/stores/page-settings';
  import { userStore } from '@client/stores/user';

  import { QueryParams } from '@client/utils/query-params';

  import { STAFF_REQUEST_PARAMS, STAFF_SEARCH_FIELDS } from '@client/enums';

  import { debounce } from '@common/utils/debounce';
  import { deepCopy, merge } from '@common/utils/objects';

  import { CreateDomainModal, ManageMDMAccountsModal } from './modals';

  const { basepath } = getContext('config');

  const queryClient = useQueryClient();

  let showFilters = true;
  let showCreateDomainModal = false;
  let showManageMDMAccountsModal = false;

  let domains = [];
  let results = 0;
  let totalRecords = 0;
  let queryParams = deepCopy(merge({}, $staffSettingsStore.params || { ...STAFF_REQUEST_PARAMS }));

  let searchQueryParam = queryParams.query;
  let selectedSearchField = 'query';
  let fields = [...STAFF_SEARCH_FIELDS];
  let searchInputValue = '';

  let [searchContext, searchValue] = (QueryParams.get('search') || '').split(':');

  if (searchContext && typeof searchContext !== 'string' && searchContext !== undefined) {
    searchContext = String(searchContext);
  }

  if (searchValue && typeof searchValue !== 'string' && searchValue !== undefined) {
    searchValue = String(searchValue);
  }

  if (!searchContext && !searchValue) {
    if (queryParams.billingid.length) {
      searchQueryParam = queryParams.billingid;
      selectedSearchField = 'billingid';
      queryParams.query = '';
    } else {
      queryParams.billingid = '';
    }
  }

  if (searchContext && searchValue) {
    queryParams[searchContext] = searchValue;
    selectedSearchField = searchContext;
    searchInputValue = searchValue;

    fields = fields.map((field) => {
      field.selected = field.value === searchContext;
      return field;
    });
  } else {
    searchInputValue = queryParams[selectedSearchField] || '';
  }

  queryParams.page = searchValue?.length ? 1 : QueryParams.get('page') || queryParams.page;
  queryParams.perPage = QueryParams.get('perpage') || queryParams.perPage;

  const updateQueryParamsAndRefetch = () => {
    queryClient.setQueryData(['staff', queryParams], (oldData) => ({
      ...oldData,
      ...queryParams
    }));

    queryClient.invalidateQueries(['staff', queryParams]);
  };

  const updateQueryParams = (params) => {
    queryParams = { ...params };
    $staffSettingsStore.params = queryParams;
    updateQueryParamsAndRefetch();
  };

  const onChangePage = (page) => updateQueryParams({ ...queryParams, page });
  const onChangeRecords = (perPage) => updateQueryParams({ ...queryParams, perPage });
  const onChangeSearch = debounce((value, field) => {
    queryClient.cancelQueries(['staff', queryParams]);

    const params = {
      query: '',
      billingid: ''
    };

    const trimmedValue = value.trim();

    if (field) {
      params[field.value] = trimmedValue;
    } else {
      params.query = trimmedValue;
    }

    updateQueryParams({ ...queryParams, ...params, page: 1 });
  }, 500);

  let usageData = {};

  const getEBCData = async () => {
    const response = await DomainService.getEBC();

    // On failure, APITransport resolves with an error object ({ status, message }),
    // so guard on billingidSeats before indexing '60' — otherwise a failed EBC call
    // (e.g. a backend 500) throws an unhandled TypeError here.
    if (response && response?.billingidSeats) {
      usageData = response.billingidSeats['60'] || {};
    }

    return response;
  };

  const onBillingIdClick = (billingid) => {
    queryParams.billingid = billingid;
    queryParams.query = '';
    queryParams.page = 1;

    selectedSearchField = 'billingid';
    queryParams[selectedSearchField] = billingid;
    searchInputValue = billingid;

    searchQueryParam = queryParams.billingid;

    QueryParams.set('search', `${selectedSearchField}:${billingid}`);

    fields = fields.map((field) => {
      field.selected = field.value === selectedSearchField;
      return field;
    });

    updateQueryParams(queryParams);
  };

  const queryFn = async () => {
    const response = await DomainsService.getAll(queryParams);

    if (response.status === 500) {
      domains = [];
      results = 0;
      totalRecords = 0;
      return { data: [], headers: {} };
    }

    domains = response.data || [];
    results = (response.headers && response.headers['x-search-total']) || 0;
    totalRecords = (response.headers && response.headers['x-filtered-total']) || domains.length;

    return response;
  };

  updateQueryParams(queryParams);

  const query = createQuery({ queryKey: ['staff', queryParams], queryFn });

  getEBCData();
</script>

<div id="domains" class="page" class:with-footer={$query.data?.data}>
  <Loader backdrop show={$query.isLoading || $query.isFetching} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/staff" use:link>{$format('label.STAFF_TOOLS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.DOMAINS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="6" sm="6" md="8">
          <h1 class="page-title">{$format('label.MANAGE_DOMAINS')}</h1>
        </Col>
        <Col xs="6" sm="6" md="4" class="text-right">
          <ButtonDropdown>
            <DropdownToggle color="secondary" caret>{$format('label.ACTIONS')}</DropdownToggle>
            <DropdownMenu>
              <DropdownItem on:click={() => (showCreateDomainModal = true)}
                >{$format('label.CREATE_DOMAIN')}</DropdownItem
              >
              <DropdownItem divider />
              <DropdownItem on:click={() => (showManageMDMAccountsModal = true)}
                >{$format('label.MANAGE_MDM_ACCOUNTS')}</DropdownItem
              >
            </DropdownMenu>
          </ButtonDropdown>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="toolbar">
      <Container fluid>
        <Row>
          <Col lg="8" md="6" sm="12">
            <FilterBar bind:show={showFilters}>
              <div class="toolbar-left" class:collapsed={!showFilters}>
                <SearchFilter
                  bind:search={searchInputValue}
                  bind:selectedSearchField
                  bind:searchFields={fields}
                  onChange={onChangeSearch}
                />
              </div>
            </FilterBar>
          </Col>
          <Col lg="4" md="6" sm="12">
            <div class="toolbar-right">
              {#if domains.length !== results}
                <small class="count">
                  {$format('label.MATCHES_X_OF_X_DOMAINS', {
                    values: {
                      filtered: Number(totalRecords).toLocaleString(),
                      total: Number(results).toLocaleString()
                    }
                  })}
                </small>
              {/if}
            </div>
          </Col>
        </Row>
      </Container>
    </div>

    {#if domains}
      <div class="domain-cards">
        <Container fluid>
          <Row>
            <Col>
              <div class="horizontal-cards">
                {#each domains as domain}
                  <DomainCard
                    {domain}
                    {onBillingIdClick}
                    selected={$userStore.domain === domain.domainname}
                    bind:usage={usageData[domain?.domainsettings?.billing_id]}
                  />
                {/each}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    {/if}
  </div>

  {#if domains.length}
    <div class="page-footer">
      <Pagination
        bind:currentPage={queryParams.page}
        bind:perPage={queryParams.perPage}
        {totalRecords}
        {onChangeRecords}
        {onChangePage}
      />
    </div>
  {/if}
</div>

<CreateDomainModal bind:show={showCreateDomainModal} />

<ManageMDMAccountsModal bind:show={showManageMDMAccountsModal} />

<style lang="scss" src="./domains.scss"></style>
